import React from 'react';
import { Box, Typography, Button, Grid } from '@mui/material';
import Cookies from 'js-cookie';
import { styled } from '@mui/material/styles';
import { Fade, Slide } from 'react-awesome-reveal';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar.tsx';
import DashBoardNavBar from '../components/DashBoardNavBar.tsx';
import Footer from '../components/Footer.tsx';
import { useThemeContext } from '../contexts/ThemeContext.tsx'; // Importáld a context-et

// Háttér és egyéb animációk
const keyframes = `
@keyframes backgroundMove {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}
@keyframes shine {
  0% { box-shadow: 0 0 10px #ff4081; }
  50% { box-shadow: 0 0 20px #ff80ab, 0 0 30px #ff4081; }
  100% { box-shadow: 0 0 10px #ff4081; }
}
`;

// Dinamikus konténer a téma alapján
const Container = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'darkMode',
})<{ darkMode: boolean }>(({ darkMode }) => ({
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  textAlign: 'center',
  padding: '16px',
  position: 'relative',
  overflow: 'hidden',
  // SÖTÉT MÓD: Fekete -> Mélyvörös | VILÁGOS MÓD: Kék -> Cián
  background: darkMode 
    ? 'linear-gradient(45deg, #000000 10%, #5e0b0b 50%, #000000 90%)' 
    : 'linear-gradient(45deg, #25379c 30%, #21cbf3 90%)',
  animation: 'backgroundMove 15s ease infinite',
  backgroundSize: '200% 200%',
  zIndex: 0,
  transition: 'background 0.5s ease', // Sima átmenet a váltáskor
}));

const Heading = styled(Typography)({
  fontSize: '3rem',
  fontWeight: 700,
  color: '#fff',
  marginBottom: '16px',
  textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
});

const SubHeading = styled(Typography)({
  fontSize: '1.2rem',
  color: '#fff',
  marginBottom: '24px',
});

const ButtonStyled = styled(Button)({
  padding: '12px 30px',
  fontSize: '1.2rem',
  borderRadius: '30px',
  backgroundColor: '#ff4081',
  color: '#fff',
  fontWeight: 'bold',
  textTransform: 'uppercase',
  transition: '0.3s ease',
  boxShadow: '0 0 10px #ff4081',
  animation: 'shine 2s infinite alternate',
  '&:hover': {
    backgroundColor: '#f50057',
    transform: 'scale(1.1)',
    boxShadow: '0 0 25px #ff80ab',
  },
});

const DescriptionText = styled('div')({
  fontSize: '1.1rem',
  lineHeight: 1.6,
  maxWidth: '800px',
  margin: '20px auto',
  color: '#E0E0E0', // Fix világosabb szürke, hogy mindkét sötét háttéren olvasható legyen
});

const FeatureHighlightsContainer = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
  gap: '20px',
  margin: '40px 0',
  width: '100%',
  maxWidth: '1000px',
});

const FeatureCard = styled('div')(({ theme }) => ({
  padding: '25px',
  borderRadius: '16px',
  // Üveghatás (Glassmorphism) a kártyáknak, hogy jól mutassanak az animált háttéren
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  color: '#fff',
  textAlign: 'center',
  boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
  '& > div': {
    fontSize: '0.9rem',
    marginTop: '10px',
    color: '#ccc',
  }
}));

const HomePage: React.FC = () => {
  const token = Cookies.get('token');
  const isAuthenticated = !!token;
  const navigate = useNavigate();
  const { darkMode } = useThemeContext(); // Lekérjük az aktuális módot

  return (
    <>
      {isAuthenticated ? <DashBoardNavBar /> : <Navbar />}
      <style>{keyframes}</style>
      <Container darkMode={darkMode} sx={{ pt: { xs: '80px', md: '100px' }, pb: '40px' }}>
        <Slide triggerOnce direction="up">
          <Heading variant="h1">Welcome to Link Learning!</Heading>
        </Slide>
  
        <Fade triggerOnce>
          <SubHeading variant="h2">
            Transform how you collect, organize and share digital treasures
          </SubHeading>
        </Fade>
  
        <Fade triggerOnce delay={100}>
          <DescriptionText>
            Link Learning revolutionizes knowledge management by combining powerful link organization with gamified learning. Create personalized collections across all topics, collaborate with communities, and earn rewards while exploring. Collect tokens through daily challenges to unlock exclusive ranks, customize your avatar, and access premium content.
          </DescriptionText>
        </Fade>
  
        <Grid container spacing={2} justifyContent="center" sx={{ mt: 2 }}>
          <Grid item>
            <Fade triggerOnce delay={200}>
              <ButtonStyled 
                variant="contained" 
                onClick={() => navigate('/linklearning')} 
              >
                🚀 let's start exploring
              </ButtonStyled>
            </Fade>
          </Grid>
        </Grid>
  
        <FeatureHighlightsContainer>
          <Fade triggerOnce delay={400}>
            <FeatureCard>
              <Typography variant="h6">🌐 Multi-Category Libraries</Typography>
              <div>Organize links with smart tagging system</div>
            </FeatureCard>
          </Fade>
          <Fade triggerOnce delay={500}>
            <FeatureCard>
              <Typography variant="h6">🎮 Interactive Learning</Typography>
              <div>Earn tokens by completing knowledge quests</div>
            </FeatureCard>
          </Fade>
        </FeatureHighlightsContainer>
      </Container>
      <Footer/>
    </>
  );
};

export default HomePage;